import React, { createContext, useContext, useState, useEffect } from 'react';
import { ethers } from 'ethers';
import toast from 'react-hot-toast';
import { CONTRACT_ADDRESSES_V3, NETWORK_INFO as STATIC_NETWORK_INFO } from '../config/contracts-v3';
import i18n from '../i18n';

const Web3ContextV3 = createContext();

export const useWeb3V3 = () => {
  const context = useContext(Web3ContextV3);
  if (!context) {
    throw new Error('useWeb3V3 must be used within a Web3ProviderV3');
  }
  return context;
};

// ============ Payment Method Enum ============
export const PaymentMethod = {
  USDT: 0,
  ETH: 1
};

// ============ Runtime Config (from public/config.js) ============
// Priority: window.NFT_LOTTERY_CONFIG > environment variables > static config
const getRuntimeConfig = () => {
  if (typeof window !== 'undefined' && window.NFT_LOTTERY_CONFIG) {
    console.log('Using runtime config from config.js');
    return window.NFT_LOTTERY_CONFIG;
  }
  console.log('Using static config (fallback)');
  return {
    CONTRACT_ADDRESSES: CONTRACT_ADDRESSES_V3,
    NETWORK_INFO: STATIC_NETWORK_INFO
  };
};

const RUNTIME_CONFIG = getRuntimeConfig();

// ============ Contract Address Configuration (V3) ============
const CONTRACT_ADDRESSES = {
  systemConfig: process.env.REACT_APP_SYSTEM_CONFIG_ADDRESS || RUNTIME_CONFIG.CONTRACT_ADDRESSES.systemConfig,
  usdtToken: process.env.REACT_APP_USDT_TOKEN_ADDRESS || RUNTIME_CONFIG.CONTRACT_ADDRESSES.usdtToken,
  lotteryNFT: process.env.REACT_APP_LOTTERY_NFT_V3_ADDRESS || RUNTIME_CONFIG.CONTRACT_ADDRESSES.lotteryNFT,
  feeManager: process.env.REACT_APP_FEE_MANAGER_ADDRESS || RUNTIME_CONFIG.CONTRACT_ADDRESSES.feeManager,
  dividendManager: process.env.REACT_APP_DIVIDEND_MANAGER_ADDRESS || RUNTIME_CONFIG.CONTRACT_ADDRESSES.dividendManager,
  prizePool: process.env.REACT_APP_PRIZE_POOL_ADDRESS || RUNTIME_CONFIG.CONTRACT_ADDRESSES.prizePool,
  marketplace: process.env.REACT_APP_MARKETPLACE_ADDRESS || RUNTIME_CONFIG.CONTRACT_ADDRESSES.marketplace
};

// Export NETWORK_INFO for use in other components
export const NETWORK_INFO = RUNTIME_CONFIG.NETWORK_INFO;

// Debug: Print contract addresses
console.log('V3 Contract Addresses:', CONTRACT_ADDRESSES);
console.log('Marketplace address:', CONTRACT_ADDRESSES.marketplace);
console.log('RPC URL:', NETWORK_INFO.rpcUrl);

// ============ Contract ABIs (Simplified) ============

// SystemConfig ABI
const SYSTEM_CONFIG_ABI = [
  'function getMintPrice() view returns (uint256 usdtPrice, uint256 ethPrice, bool usdtEnabled, bool ethEnabled)',
  'function getFeeDistribution() view returns (uint256 feePercent, uint256 commissionPercent, uint256 dividendPercent, uint256 prizePercent)',
  'function getPrizeDistribution() view returns (uint256 grandPrize, uint256 firstPrize, uint256 secondPrize, uint256 thirdPrize)',
  'function getOperationWallet() view returns (address)',
  'function getCommissionWallet() view returns (address)',
  'function getDrawInterval() view returns (uint256)',
  'function getTotalNFTSupply() view returns (uint256)',
  'event PriceConfigUpdated(uint256 usdtPrice, uint256 ethPrice, bool usdtEnabled, bool ethEnabled)',
  'event FeeDistributionUpdated(uint256 feePercent, uint256 commissionPercent, uint256 dividendPercent, uint256 prizePercent)'
];

// USDT Token ABI (6 decimals)
const USDT_TOKEN_ABI = [
  'function name() view returns (string)',
  'function symbol() view returns (string)',
  'function decimals() view returns (uint8)',
  'function totalSupply() view returns (uint256)',
  'function balanceOf(address) view returns (uint256)',
  'function transfer(address, uint256) returns (bool)',
  'function approve(address, uint256) returns (bool)',
  'function allowance(address, address) view returns (uint256)',
  'event Transfer(address indexed from, address indexed to, uint256 value)',
  'event Approval(address indexed owner, address indexed spender, uint256 value)'
];

// LotteryNFT_V3 ABI
const LOTTERY_NFT_V3_ABI = [
  'function name() view returns (string)',
  'function symbol() view returns (string)',
  'function totalSupply() view returns (uint256)',
  'function totalMinted() view returns (uint256)',
  'function balanceOf(address) view returns (uint256)',
  'function ownerOf(uint256) view returns (address)',
  'function getMetadata(uint256) view returns (uint256 number, string signature, uint256 mintTimestamp, uint256 currentDrawId, uint256 totalDividends, uint256 totalPrizes, bool exists)',
  'function numberToTokenId(uint256) view returns (uint256)',
  'function isNumberAvailable(uint256) view returns (bool)',
  'function getUserTokenIds(address) view returns (uint256[])',
  // NFT approval methods (required for marketplace)
  'function approve(address to, uint256 tokenId)',
  'function getApproved(uint256 tokenId) view returns (address)',
  'function setApprovalForAll(address operator, bool approved)',
  'function isApprovedForAll(address owner, address operator) view returns (bool)',
  'event Transfer(address indexed from, address indexed to, uint256 indexed tokenId)',
  'event Approval(address indexed owner, address indexed approved, uint256 indexed tokenId)',
  'event ApprovalForAll(address indexed owner, address indexed operator, bool approved)',
  'event NFTMinted(address indexed to, uint256 indexed tokenId, uint256 number, string signature, uint256 drawId)'
];

// FeeManager ABI
const FEE_MANAGER_ABI = [
  'function mintWithNumberUSDT(uint256 number, string memory signature) returns (uint256)',
  'function mintWithNumberETH(uint256 number, string memory signature) payable returns (uint256)',
  'function mintRandomUSDT(string memory signature) returns (uint256)',
  'function mintRandomETH(string memory signature) payable returns (uint256)',
  'function getDrawSales(uint256 drawId) view returns (tuple(uint256 drawId, uint256 totalSales, uint256 usdtSales, uint256 ethSales, uint256 nftCount, uint256 feeCollected, uint256 commissionCollected, uint256 dividendPool, uint256 prizePool))',
  'function getCurrentPrice(uint8 paymentMethod) view returns (uint256)',
  'event NFTPurchased(address indexed buyer, uint256 indexed tokenId, uint256 number, uint8 paymentMethod, uint256 price, uint256 drawId)',
  'event RandomNumberGenerated(uint256 indexed tokenId, uint256 number)'
];

// DividendManager ABI
const DIVIDEND_MANAGER_ABI = [
  'function getDrawDividend(uint256 drawId) view returns (tuple(uint256 drawId, uint256 totalDividendPool, uint256 soldNFTCount, uint256 perNFTDividend, uint256 totalClaimed, uint256 timestamp, bool finalized))',
  'function getClaimableDividends(address user, uint256[] memory drawIds) view returns (uint256)',
  'function claimDividend(uint256 drawId) returns (uint256)',
  'function claimDividendsBatch(uint256[] memory drawIds) returns (uint256)',
  'function hasClaimedDividend(uint256 drawId, uint256 tokenId) view returns (bool)',
  'function getUnclaimedDrawIds(address user) view returns (uint256[])',
  'event DividendFinalized(uint256 indexed drawId, uint256 totalPool, uint256 soldNFTCount, uint256 perNFTDividend)',
  'event DividendClaimed(address indexed user, uint256 indexed tokenId, uint256 indexed drawId, uint256 amount)'
];

// PrizePool ABI
const PRIZE_POOL_ABI = [
  'function requestDraw() returns (uint256)',
  'function getDrawResult(uint256 drawId) view returns (tuple(uint256 drawId, uint256 winningNumber, uint256 verirandRequestId, uint256 timestamp, uint256 totalPrizePool, uint256 grandPrizePool, uint256 firstPrizePool, uint256 secondPrizePool, uint256 thirdPrizePool, uint256 grandWinnerCount, uint256 firstWinnerCount, uint256 secondWinnerCount, uint256 thirdWinnerCount, bool finalized))',
  'function getWinnerInfo(uint256 drawId, uint256 tokenId) view returns (tuple(uint256 tokenId, address owner, uint8 level, uint256 prizeAmount, bool claimed))',
  'function claimPrize(uint256 drawId, uint256 tokenId)',
  'function claimPrizesBatch(uint256[] memory drawIds, uint256[] memory tokenIds)',
  'function checkMatch(uint256 tokenId, uint256 drawId) view returns (uint8)',
  'function getTokenWinningDraws(uint256 tokenId) view returns (uint256[])',
  'function currentDrawId() view returns (uint256)',
  'function lastDrawTime() view returns (uint256)',
  'event DrawRequested(uint256 indexed drawId, uint256 requestId)',
  'event DrawCompleted(uint256 indexed drawId, uint256 winningNumber, uint256 timestamp)',
  'event PrizeClaimed(address indexed user, uint256 indexed tokenId, uint256 indexed drawId, uint8 level, uint256 amount)'
];

// Marketplace ABI
const MARKETPLACE_ABI = [
  'function createOrderUSDT(uint256 tokenId, uint256 price) returns (uint256)',
  'function createOrderETH(uint256 tokenId, uint256 price) returns (uint256)',
  'function buyWithUSDT(uint256 orderId)',
  'function buyWithETH(uint256 orderId) payable',
  'function cancelOrder(uint256 orderId)',
  'function getOrder(uint256 orderId) view returns (tuple(uint256 orderId, address seller, uint256 tokenId, uint256 price, uint8 paymentMethod, uint8 status, uint256 timestamp))',
  // Returns Order struct array
  'function getActiveOrders() view returns (tuple(uint256 orderId, address seller, uint256 tokenId, uint256 price, uint8 paymentMethod, uint8 status, uint256 timestamp)[])',
  'function getUserOrders(address user) view returns (tuple(uint256 orderId, address seller, uint256 tokenId, uint256 price, uint8 paymentMethod, uint8 status, uint256 timestamp)[])',
  'function getActiveOrderCount() view returns (uint256)',
  'function getTotalTradeCount() view returns (uint256)',
  'function getRecentTrades(uint256 limit) view returns (tuple(uint256 orderId, address seller, address buyer, uint256 tokenId, uint256 price, uint8 paymentMethod, uint256 platformFee, uint256 timestamp)[])',
  'event OrderCreated(uint256 indexed orderId, address indexed seller, uint256 indexed tokenId, uint256 price, uint8 paymentMethod)',
  'event OrderCancelled(uint256 indexed orderId, address indexed seller, uint256 indexed tokenId)',
  'event OrderExecuted(uint256 indexed orderId, address indexed seller, address indexed buyer, uint256 tokenId, uint256 price, uint8 paymentMethod, uint256 platformFee)'
];

// ============ Provider Component ============

export const Web3ProviderV3 = ({ children }) => {
  const [account, setAccount] = useState(null);
  const [provider, setProvider] = useState(null);
  const [signer, setSigner] = useState(null);
  const [contracts, setContracts] = useState({});
  const [isConnecting, setIsConnecting] = useState(false);
  const [network, setNetwork] = useState(null);

  // V3 system state
  const [systemConfig, setSystemConfig] = useState({
    usdtPrice: '0',
    ethPrice: '0',
    usdtEnabled: false,
    ethEnabled: false,
    drawInterval: '0'
  });

  // User balances
  const [balances, setBalances] = useState({
    usdt: '0',
    eth: '0'
  });

  // NFT statistics
  const [nftStats, setNftStats] = useState({
    totalMinted: '0',
    totalSupply: '100000',
    currentDrawId: '0',
    lastDrawTime: '0',
    prizePoolBalance: '0',
    dividendPoolBalance: '0',
    projectFee: '0'
  });

  // Backend API URL for shared prize pool data
  const BACKEND_API_URL = process.env.REACT_APP_BACKEND_API_URL || 'http://localhost:8085';

  // ============ Wallet Connection ============

  const checkConnection = async () => {
    if (typeof window.ethereum !== 'undefined') {
      try {
        const accounts = await window.ethereum.request({ method: 'eth_accounts' });
        if (accounts.length > 0) {
          await connectWallet();
        }
      } catch (error) {
        console.error('Failed to check connection:', error);
      }
    }
  };

  const connectWallet = async () => {
    if (typeof window.ethereum === 'undefined') {
      toast.error('Please install MetaMask wallet');
      return;
    }

    try {
      setIsConnecting(true);

      const accounts = await window.ethereum.request({
        method: 'eth_requestAccounts',
      });

      if (accounts.length > 0) {
        const account = accounts[0];
        setAccount(account);

        const provider = new ethers.BrowserProvider(window.ethereum);
        const signer = await provider.getSigner();
        const network = await provider.getNetwork();

        setProvider(provider);
        setSigner(signer);
        setNetwork(network);

        // Initialize contracts (pass account to avoid state update delay)
        await initializeContracts(signer, account, provider);

        // Setup event listeners
        setupEventListeners();

        toast.success(i18n.t('common.walletConnected'));
      }
    } catch (error) {
      console.error('Failed to connect wallet:', error);
      toast.error('Failed to connect wallet: ' + error.message);
    } finally {
      setIsConnecting(false);
    }
  };

  const disconnectWallet = () => {
    setAccount(null);
    setProvider(null);
    setSigner(null);
    setContracts({});
    setNetwork(null);
    toast.success(i18n.t('common.walletDisconnected'));
  };

  // ============ Contract Initialization ============

  const initializeContracts = async (signer, userAccount, web3Provider) => {
    try {
      const systemConfigContract = new ethers.Contract(
        CONTRACT_ADDRESSES.systemConfig,
        SYSTEM_CONFIG_ABI,
        signer
      );

      const usdtToken = new ethers.Contract(
        CONTRACT_ADDRESSES.usdtToken,
        USDT_TOKEN_ABI,
        signer
      );

      const lotteryNFT = new ethers.Contract(
        CONTRACT_ADDRESSES.lotteryNFT,
        LOTTERY_NFT_V3_ABI,
        signer
      );

      const feeManager = new ethers.Contract(
        CONTRACT_ADDRESSES.feeManager,
        FEE_MANAGER_ABI,
        signer
      );

      const dividendManager = new ethers.Contract(
        CONTRACT_ADDRESSES.dividendManager,
        DIVIDEND_MANAGER_ABI,
        signer
      );

      const prizePool = new ethers.Contract(
        CONTRACT_ADDRESSES.prizePool,
        PRIZE_POOL_ABI,
        signer
      );

      const marketplace = new ethers.Contract(
        CONTRACT_ADDRESSES.marketplace,
        MARKETPLACE_ABI,
        signer
      );

      setContracts({
        systemConfig: systemConfigContract,
        usdtToken,
        lotteryNFT,
        feeManager,
        dividendManager,
        prizePool,
        marketplace
      });

      // Load system config and data (using passed params instead of state)
      await loadSystemConfig(systemConfigContract, lotteryNFT, prizePool);
      if (userAccount && web3Provider) {
        await loadBalances(usdtToken, web3Provider, userAccount);
      }

    } catch (error) {
      console.error('Failed to initialize contracts:', error);
      toast.error('Contract initialization failed');
    }
  };

  // ============ Load Data ============

  const loadSystemConfig = async (systemConfigContract, lotteryNFT, prizePool) => {
    try {
      const [mintPrice, drawInterval, currentDrawId, lastDrawTime, totalMinted, totalSupply] = await Promise.all([
        systemConfigContract.getMintPrice(),
        systemConfigContract.getDrawInterval(),
        prizePool.currentDrawId(),
        prizePool.lastDrawTime(),
        lotteryNFT.totalMinted(),
        systemConfigContract.getTotalNFTSupply()
      ]);

      setSystemConfig({
        usdtPrice: ethers.formatUnits(mintPrice[0], 6),
        ethPrice: ethers.formatEther(mintPrice[1]),
        usdtEnabled: mintPrice[2],
        ethEnabled: mintPrice[3],
        drawInterval: drawInterval.toString()
      });

      // Fetch prize pool data from backend API (shared with go-uniapp)
      let prizePoolBalance = '0';
      let dividendPoolBalance = '0';
      let projectFee = '0';
      try {
        const response = await fetch(`${BACKEND_API_URL}/api/powerball/prize-pool/total`);
        if (response.ok) {
          const data = await response.json();
          if (data.code === 1 && data.data) {
            prizePoolBalance = data.data.prize_pool?.toFixed(2) || '0';
            dividendPoolBalance = data.data.dividend_pool?.toFixed(2) || '0';
            projectFee = data.data.project_fee?.toFixed(2) || '0';
            console.log('Prize pool data from backend:', data.data);
          }
        }
      } catch (e) {
        console.warn('Failed to get prize pool from backend API:', e);
        // Fallback: try to get from smart contract
        try {
          const usdtContract = new ethers.Contract(
            CONTRACT_ADDRESSES.usdtToken,
            USDT_TOKEN_ABI,
            prizePool.runner
          );
          const balance = await usdtContract.balanceOf(CONTRACT_ADDRESSES.prizePool);
          prizePoolBalance = ethers.formatUnits(balance, 6);
        } catch (contractErr) {
          console.warn('Failed to get prize pool from contract:', contractErr);
        }
      }

      setNftStats({
        totalMinted: totalMinted.toString(),
        totalSupply: totalSupply.toString(),
        currentDrawId: currentDrawId.toString(),
        lastDrawTime: lastDrawTime.toString(),
        prizePoolBalance: prizePoolBalance,
        dividendPoolBalance: dividendPoolBalance,
        projectFee: projectFee
      });

    } catch (error) {
      console.error('Failed to load system config:', error);
    }
  };

  const loadBalances = async (usdtToken, provider, userAddress) => {
    try {
      const [usdtBalance, ethBalance] = await Promise.all([
        usdtToken.balanceOf(userAddress),
        provider.getBalance(userAddress)
      ]);

      setBalances({
        usdt: ethers.formatUnits(usdtBalance, 6),
        eth: ethers.formatEther(ethBalance)
      });
    } catch (error) {
      console.error('Failed to load balance:', error);
    }
  };

  // ============ Event Listeners ============

  const setupEventListeners = () => {
    if (window.ethereum) {
      window.ethereum.on('accountsChanged', handleAccountsChanged);
      window.ethereum.on('chainChanged', handleChainChanged);
    }
  };

  const handleAccountsChanged = (accounts) => {
    if (accounts.length === 0) {
      disconnectWallet();
    } else if (accounts[0] !== account) {
      setAccount(accounts[0]);
      window.location.reload();
    }
  };

  const handleChainChanged = () => {
    window.location.reload();
  };

  // ============ NFT Minting ============

  /**
   * Mint random NFT (USDT payment)
   */
  const mintRandomUSDT = async (signature = '') => {
    if (!contracts.feeManager || !account) {
      throw new Error('Please connect wallet first');
    }

    try {
      const price = ethers.parseUnits(systemConfig.usdtPrice, 6);

      // Check balance
      const balance = await contracts.usdtToken.balanceOf(account);
      if (balance < price) {
        throw new Error('Insufficient USDT balance');
      }

      // Check and approve
      const allowance = await contracts.usdtToken.allowance(account, CONTRACT_ADDRESSES.feeManager);
      if (allowance < price) {
        toast.loading('Approving USDT...');
        const approveTx = await contracts.usdtToken.approve(CONTRACT_ADDRESSES.feeManager, price);
        await approveTx.wait();
        toast.dismiss();
        toast.success('USDT approved successfully');
      }

      // Mint NFT
      toast.loading('Minting NFT...');
      const tx = await contracts.feeManager.mintRandomUSDT(signature);
      const receipt = await tx.wait();

      toast.dismiss();
      toast.success(`NFT minted successfully! Paid: ${systemConfig.usdtPrice} USDT`);

      // Refresh data
      await loadBalances(contracts.usdtToken, provider, account);

      return receipt;
    } catch (error) {
      toast.dismiss();
      console.error('Failed to mint NFT:', error);
      throw error;
    }
  };

  /**
   * Mint random NFT (ETH payment)
   */
  const mintRandomETH = async (signature = '') => {
    if (!contracts.feeManager || !account) {
      throw new Error('Please connect wallet first');
    }

    try {
      const price = ethers.parseEther(systemConfig.ethPrice);

      // Check balance
      const balance = await provider.getBalance(account);
      if (balance < price) {
        throw new Error('Insufficient ETH balance');
      }

      // Mint NFT
      toast.loading('Minting NFT...');
      const tx = await contracts.feeManager.mintRandomETH(signature, { value: price });
      const receipt = await tx.wait();

      toast.dismiss();
      toast.success(`NFT minted successfully! Paid: ${systemConfig.ethPrice} ETH`);

      // Refresh data
      await loadBalances(contracts.usdtToken, provider, account);

      return receipt;
    } catch (error) {
      toast.dismiss();
      console.error('Failed to mint NFT:', error);
      throw error;
    }
  };

  /**
   * Mint with custom number (USDT payment)
   */
  const mintWithNumberUSDT = async (number, signature = '') => {
    if (!contracts.feeManager || !account) {
      throw new Error('Please connect wallet first');
    }

    try {
      // Check if number is available
      const isAvailable = await contracts.lotteryNFT.isNumberAvailable(number);
      if (!isAvailable) {
        throw new Error('Number already taken');
      }

      const price = ethers.parseUnits(systemConfig.usdtPrice, 6);

      // Check balance
      const balance = await contracts.usdtToken.balanceOf(account);
      if (balance < price) {
        throw new Error('Insufficient USDT balance');
      }

      // Check and approve
      const allowance = await contracts.usdtToken.allowance(account, CONTRACT_ADDRESSES.feeManager);
      if (allowance < price) {
        toast.loading('Approving USDT...');
        const approveTx = await contracts.usdtToken.approve(CONTRACT_ADDRESSES.feeManager, price);
        await approveTx.wait();
        toast.dismiss();
        toast.success('USDT approved successfully');
      }

      // Mint NFT
      toast.loading(`Minting NFT #${number.toString().padStart(6, '0')}...`);
      const tx = await contracts.feeManager.mintWithNumberUSDT(number, signature);
      const receipt = await tx.wait();

      toast.dismiss();
      toast.success(`NFT minted successfully! Number: ${number.toString().padStart(6, '0')}`);

      // Refresh data
      await loadBalances(contracts.usdtToken, provider, account);

      return receipt;
    } catch (error) {
      toast.dismiss();
      console.error('Failed to mint NFT:', error);
      throw error;
    }
  };

  /**
   * Mint with custom number (ETH payment)
   */
  const mintWithNumberETH = async (number, signature = '') => {
    if (!contracts.feeManager || !account) {
      throw new Error('Please connect wallet first');
    }

    try {
      // Check if number is available
      const isAvailable = await contracts.lotteryNFT.isNumberAvailable(number);
      if (!isAvailable) {
        throw new Error('Number already taken');
      }

      const price = ethers.parseEther(systemConfig.ethPrice);

      // Check balance
      const balance = await provider.getBalance(account);
      if (balance < price) {
        throw new Error('Insufficient ETH balance');
      }

      // Mint NFT
      toast.loading(`Minting NFT #${number.toString().padStart(6, '0')}...`);
      const tx = await contracts.feeManager.mintWithNumberETH(number, signature, { value: price });
      const receipt = await tx.wait();

      toast.dismiss();
      toast.success(`NFT minted successfully! Number: ${number.toString().padStart(6, '0')}`);

      // Refresh data
      await loadBalances(contracts.usdtToken, provider, account);

      return receipt;
    } catch (error) {
      toast.dismiss();
      console.error('Failed to mint NFT:', error);
      throw error;
    }
  };

  // ============ NFT Query Functions ============

  /**
   * Get all NFTs owned by user
   */
  const getUserNFTs = async (address = account) => {
    if (!contracts.lotteryNFT || !address) return [];

    try {
      const tokenIds = await contracts.lotteryNFT.getUserTokenIds(address);
      const nfts = [];

      for (const tokenId of tokenIds) {
        const metadata = await contracts.lotteryNFT.getMetadata(tokenId);
        // metadata returns array: [number, signature, mintTimestamp, currentDrawId, totalDividends, totalPrizes, exists]
        const [number, signature, mintTimestamp, currentDrawId, totalDividends, totalPrizes, exists] = metadata;
        nfts.push({
          tokenId: tokenId.toString(),
          number: number.toString().padStart(6, '0'),
          signature: signature || '',
          mintTimestamp: mintTimestamp.toString(),
          drawId: currentDrawId.toString(),
          totalDividends: ethers.formatUnits(totalDividends, 6),
          totalPrizes: ethers.formatUnits(totalPrizes, 6),
          exists: exists
        });
      }

      return nfts;
    } catch (error) {
      console.error('Failed to get user NFTs:', error);
      return [];
    }
  };

  // ============ Dividend Functions ============

  /**
   * Query claimable dividends
   */
  const getClaimableDividends = async (drawIds) => {
    if (!contracts.dividendManager || !account) return '0';

    try {
      const amount = await contracts.dividendManager.getClaimableDividends(account, drawIds);
      return ethers.formatUnits(amount, 6);
    } catch (error) {
      console.error('Failed to query dividends:', error);
      return '0';
    }
  };

  /**
   * Claim dividend (single draw)
   */
  const claimDividend = async (drawId) => {
    if (!contracts.dividendManager || !account) {
      throw new Error('Please connect wallet first');
    }

    try {
      toast.loading('Claiming dividend...');
      const tx = await contracts.dividendManager.claimDividend(drawId);
      await tx.wait();

      toast.dismiss();
      toast.success('Dividend claimed successfully!');

      return tx;
    } catch (error) {
      toast.dismiss();
      console.error('Failed to claim dividend:', error);
      throw error;
    }
  };

  /**
   * Batch claim dividends
   */
  const claimDividendsBatch = async (drawIds) => {
    if (!contracts.dividendManager || !account) {
      throw new Error('Please connect wallet first');
    }

    try {
      toast.loading('Batch claiming dividends...');
      const tx = await contracts.dividendManager.claimDividendsBatch(drawIds);
      await tx.wait();

      toast.dismiss();
      toast.success(`Successfully claimed ${drawIds.length} dividends!`);

      return tx;
    } catch (error) {
      toast.dismiss();
      console.error('Failed to batch claim dividends:', error);
      throw error;
    }
  };

  /**
   * Get user's unclaimed draw IDs
   */
  const getUserUnclaimedDraws = async (address = account) => {
    if (!contracts.dividendManager || !address) return [];

    try {
      const drawIds = await contracts.dividendManager.getUnclaimedDrawIds(address);
      return drawIds.map(id => id.toString());
    } catch (error) {
      console.error('Failed to get unclaimed draws:', error);
      return [];
    }
  };

  // ============ Prize Pool Functions ============

  /**
   * Get draw result
   */
  const getDrawResult = async (drawId) => {
    if (!contracts.prizePool) return null;

    try {
      const result = await contracts.prizePool.getDrawResult(drawId);
      return {
        drawId: result.drawId.toString(),
        winningNumber: result.winningNumber.toString().padStart(6, '0'),
        timestamp: result.timestamp.toString(),
        totalPrizePool: ethers.formatUnits(result.totalPrizePool, 6),
        grandPrizePool: ethers.formatUnits(result.grandPrizePool, 6),
        firstPrizePool: ethers.formatUnits(result.firstPrizePool, 6),
        secondPrizePool: ethers.formatUnits(result.secondPrizePool, 6),
        thirdPrizePool: ethers.formatUnits(result.thirdPrizePool, 6),
        grandWinnerCount: result.grandWinnerCount.toString(),
        firstWinnerCount: result.firstWinnerCount.toString(),
        secondWinnerCount: result.secondWinnerCount.toString(),
        thirdWinnerCount: result.thirdWinnerCount.toString(),
        finalized: result.finalized
      };
    } catch (error) {
      console.error('Failed to get draw result:', error);
      return null;
    }
  };

  /**
   * Claim prize
   */
  const claimPrize = async (drawId, tokenId) => {
    if (!contracts.prizePool || !account) {
      throw new Error('Please connect wallet first');
    }

    try {
      toast.loading('Claiming prize...');
      const tx = await contracts.prizePool.claimPrize(drawId, tokenId);
      await tx.wait();

      toast.dismiss();
      toast.success('Prize claimed successfully!');

      return tx;
    } catch (error) {
      toast.dismiss();
      console.error('Failed to claim prize:', error);
      throw error;
    }
  };

  /**
   * Batch claim prizes
   */
  const claimPrizesBatch = async (drawIds, tokenIds) => {
    if (!contracts.prizePool || !account) {
      throw new Error('Please connect wallet first');
    }

    try {
      toast.loading('Batch claiming prizes...');
      const tx = await contracts.prizePool.claimPrizesBatch(drawIds, tokenIds);
      await tx.wait();

      toast.dismiss();
      toast.success(`Successfully claimed ${drawIds.length} prizes!`);

      return tx;
    } catch (error) {
      toast.dismiss();
      console.error('Failed to batch claim prizes:', error);
      throw error;
    }
  };

  // ============ Marketplace Functions ============

  /**
   * Create USDT order
   */
  const createOrderUSDT = async (tokenId, price) => {
    if (!contracts.marketplace || !account) {
      throw new Error('Please connect wallet first');
    }

    try {
      // Check NFT approval
      const isApproved = await contracts.lotteryNFT.isApprovedForAll(
        account,
        CONTRACT_ADDRESSES.marketplace
      );

      if (!isApproved) {
        toast.loading('Approving NFT...');
        const approveTx = await contracts.lotteryNFT.setApprovalForAll(
          CONTRACT_ADDRESSES.marketplace,
          true
        );
        await approveTx.wait();
        toast.dismiss();
        toast.success('NFT approved successfully');
      }

      toast.loading('Creating order...');
      const priceInUsdt = ethers.parseUnits(price.toString(), 6);
      const tx = await contracts.marketplace.createOrderUSDT(tokenId, priceInUsdt);
      const receipt = await tx.wait();

      toast.dismiss();
      toast.success('Order created successfully!');

      return receipt;
    } catch (error) {
      toast.dismiss();
      console.error('Failed to create order:', error);
      throw error;
    }
  };

  /**
   * Create ETH order
   */
  const createOrderETH = async (tokenId, price) => {
    if (!contracts.marketplace || !account) {
      throw new Error('Please connect wallet first');
    }

    try {
      // Check NFT approval
      const isApproved = await contracts.lotteryNFT.isApprovedForAll(
        account,
        CONTRACT_ADDRESSES.marketplace
      );

      if (!isApproved) {
        toast.loading('Approving NFT...');
        const approveTx = await contracts.lotteryNFT.setApprovalForAll(
          CONTRACT_ADDRESSES.marketplace,
          true
        );
        await approveTx.wait();
        toast.dismiss();
        toast.success('NFT approved successfully');
      }

      toast.loading('Creating order...');
      const priceInEth = ethers.parseEther(price.toString());
      const tx = await contracts.marketplace.createOrderETH(tokenId, priceInEth);
      const receipt = await tx.wait();

      toast.dismiss();
      toast.success('Order created successfully!');

      return receipt;
    } catch (error) {
      toast.dismiss();
      console.error('Failed to create order:', error);
      throw error;
    }
  };

  /**
   * Buy with USDT
   */
  const buyWithUSDT = async (orderId) => {
    if (!contracts.marketplace || !account) {
      throw new Error('Please connect wallet first');
    }

    try {
      const order = await contracts.marketplace.getOrder(orderId);

      // Check and approveUSDT
      const allowance = await contracts.usdtToken.allowance(account, CONTRACT_ADDRESSES.marketplace);
      if (allowance < order.price) {
        toast.loading('Approving USDT...');
        const approveTx = await contracts.usdtToken.approve(
          CONTRACT_ADDRESSES.marketplace,
          order.price
        );
        await approveTx.wait();
        toast.dismiss();
        toast.success('USDT approved successfully');
      }

      toast.loading('Purchasing NFT...');
      const tx = await contracts.marketplace.buyWithUSDT(orderId);
      const receipt = await tx.wait();

      toast.dismiss();
      toast.success('NFT purchased successfully!');

      // Refresh balance
      await loadBalances(contracts.usdtToken, provider, account);

      return receipt;
    } catch (error) {
      toast.dismiss();
      console.error('Failed to purchase NFT:', error);
      throw error;
    }
  };

  /**
   * Buy with ETH
   */
  const buyWithETH = async (orderId) => {
    if (!contracts.marketplace || !account) {
      throw new Error('Please connect wallet first');
    }

    try {
      const order = await contracts.marketplace.getOrder(orderId);

      toast.loading('Purchasing NFT...');
      const tx = await contracts.marketplace.buyWithETH(orderId, { value: order.price });
      const receipt = await tx.wait();

      toast.dismiss();
      toast.success('NFT purchased successfully!');

      // Refresh balance
      await loadBalances(contracts.usdtToken, provider, account);

      return receipt;
    } catch (error) {
      toast.dismiss();
      console.error('Failed to purchase NFT:', error);
      throw error;
    }
  };

  /**
   * Cancel order
   */
  const cancelOrder = async (orderId) => {
    if (!contracts.marketplace || !account) {
      throw new Error('Please connect wallet first');
    }

    try {
      toast.loading('Cancelling order...');
      const tx = await contracts.marketplace.cancelOrder(orderId);
      await tx.wait();

      toast.dismiss();
      toast.success('Order cancelled');

      return tx;
    } catch (error) {
      toast.dismiss();
      console.error('Failed to cancel order:', error);
      throw error;
    }
  };

  /**
   * Get all active orders
   */
  const getActiveOrders = async () => {
    if (!contracts.marketplace) {
      console.log('Marketplace contract not initialized');
      return [];
    }

    try {
      console.log('Calling Marketplace.getActiveOrders()...');
      console.log('Marketplace contract address:', await contracts.marketplace.getAddress());

      // Contract returns Order struct array directly
      const rawOrders = await contracts.marketplace.getActiveOrders();
      console.log('📡 Raw orders count:', rawOrders.length);
      console.log('📡 Raw orders data:', rawOrders);
      const orders = [];

      for (const order of rawOrders) {
        // paymentMethod is BigInt, need to convert to Number for comparison
        const paymentMethodNum = Number(order.paymentMethod);
        const isUSDT = paymentMethodNum === 0;

        orders.push({
          orderId: order.orderId.toString(),
          seller: order.seller,
          tokenId: order.tokenId.toString(),
          price: isUSDT
            ? ethers.formatUnits(order.price, 6)
            : ethers.formatEther(order.price),
          paymentMethod: isUSDT ? 'USDT' : 'ETH',
          status: Number(order.status),
          timestamp: order.timestamp.toString()
        });
      }

      return orders;
    } catch (error) {
      console.error('Failed to get active orders:', error);
      return [];
    }
  };

  /**
   * Get recent trade records
   */
  const getRecentTrades = async (limit = 10) => {
    if (!contracts.marketplace) return [];

    try {
      const rawTrades = await contracts.marketplace.getRecentTrades(limit);
      const trades = [];

      for (const trade of rawTrades) {
        // paymentMethod is BigInt, need to convert to Number for comparison
        const paymentMethodNum = Number(trade.paymentMethod);
        const isUSDT = paymentMethodNum === 0;

        trades.push({
          orderId: trade.orderId.toString(),
          seller: trade.seller,
          buyer: trade.buyer,
          tokenId: trade.tokenId.toString(),
          price: isUSDT
            ? ethers.formatUnits(trade.price, 6)
            : ethers.formatEther(trade.price),
          paymentMethod: isUSDT ? 'USDT' : 'ETH',
          platformFee: isUSDT
            ? ethers.formatUnits(trade.platformFee, 6)
            : ethers.formatEther(trade.platformFee),
          timestamp: trade.timestamp.toString()
        });
      }

      return trades;
    } catch (error) {
      console.error('Failed to get recent trades:', error);
      return [];
    }
  };

  // ============ Admin Functions ============

  // Contract owner address (Hardhat default account #0)
  const OWNER_ADDRESS = '0xf39Fd6e51aad88F6F4ce6aB8827279cffFb92266';

  // Check if user is admin
  const isAdmin = () => {
    return account && account.toLowerCase() === OWNER_ADDRESS.toLowerCase();
  };

  // Trigger draw
  const requestDraw = async () => {
    if (!contracts.prizePool || !account) {
      throw new Error('Please connect wallet first');
    }

    if (!isAdmin()) {
      throw new Error('Only admin can trigger draw');
    }

    try {
      toast.loading('Triggering draw...');
      const tx = await contracts.prizePool.requestDraw();
      await tx.wait();
      toast.dismiss();
      toast.success('Draw completed successfully!');

      // Refresh data
      if (contracts.systemConfig && contracts.lotteryNFT && contracts.prizePool) {
        await loadSystemConfig(contracts.systemConfig, contracts.lotteryNFT, contracts.prizePool);
      }

      return tx;
    } catch (error) {
      toast.dismiss();
      console.error('Draw failed:', error);

      // Parse error message
      if (error.message.includes('Draw interval not passed')) {
        throw new Error('Draw interval not reached, please try again later');
      }
      throw error;
    }
  };

  // Get admin info
  const getAdminInfo = async () => {
    if (!contracts.prizePool) return null;

    try {
      const [currentDrawId, lastDrawTime] = await Promise.all([
        contracts.prizePool.currentDrawId(),
        contracts.prizePool.lastDrawTime()
      ]);

      const drawInterval = await contracts.systemConfig.getDrawInterval();

      return {
        currentDrawId: currentDrawId.toString(),
        lastDrawTime: lastDrawTime.toString(),
        drawInterval: drawInterval.toString(),
        nextDrawTime: (parseInt(lastDrawTime.toString()) + parseInt(drawInterval.toString())).toString()
      };
    } catch (error) {
      console.error('Failed to get admin info:', error);
      return null;
    }
  };

  // ============ Admin Statistics Functions ============

  /**
   * Get sales statistics (by draw ID)
   */
  const getSalesStats = async (drawId) => {
    if (!contracts.feeManager) return null;

    try {
      const sales = await contracts.feeManager.getDrawSales(drawId);
      return {
        drawId: sales.drawId.toString(),
        totalSales: ethers.formatUnits(sales.totalSales, 6),
        usdtSales: ethers.formatUnits(sales.usdtSales, 6),
        ethSales: ethers.formatEther(sales.ethSales),
        nftCount: sales.nftCount.toString(),
        feeCollected: ethers.formatUnits(sales.feeCollected, 6),
        commissionCollected: ethers.formatUnits(sales.commissionCollected, 6),
        dividendPool: ethers.formatUnits(sales.dividendPool, 6),
        prizePool: ethers.formatUnits(sales.prizePool, 6)
      };
    } catch (error) {
      console.error('Failed to get sales stats:', error);
      return null;
    }
  };

  /**
   * Get sales statistics for all draws
   */
  const getAllSalesStats = async () => {
    if (!contracts.prizePool || !contracts.feeManager) return [];

    try {
      const currentDrawId = await contracts.prizePool.currentDrawId();
      const stats = [];

      for (let i = 1; i <= parseInt(currentDrawId.toString()); i++) {
        const sales = await getSalesStats(i);
        if (sales) {
          stats.push(sales);
        }
      }

      return stats;
    } catch (error) {
      console.error('Failed to get all sales stats:', error);
      return [];
    }
  };

  /**
   * Get dividend data for all draws
   */
  const getAllDividendStats = async () => {
    if (!contracts.prizePool || !contracts.dividendManager) return [];

    try {
      const currentDrawId = await contracts.prizePool.currentDrawId();
      const stats = [];

      for (let i = 1; i < parseInt(currentDrawId.toString()); i++) {
        try {
          const dividend = await contracts.dividendManager.getDrawDividend(i);
          stats.push({
            drawId: dividend.drawId.toString(),
            totalDividendPool: ethers.formatUnits(dividend.totalDividendPool, 6),
            soldNFTCount: dividend.soldNFTCount.toString(),
            perNFTDividend: ethers.formatUnits(dividend.perNFTDividend, 6),
            totalClaimed: ethers.formatUnits(dividend.totalClaimed, 6),
            timestamp: dividend.timestamp.toString(),
            finalized: dividend.finalized
          });
        } catch (e) {
          // Skip draws that are not yet finalized
        }
      }

      return stats;
    } catch (error) {
      console.error('Failed to get dividend stats:', error);
      return [];
    }
  };

  /**
   * Get NFT holder rankings
   */
  const getNFTHolderRankings = async (limit = 20) => {
    if (!contracts.lotteryNFT || !provider) return [];

    try {
      // Get all Transfer events to calculate holdings
      const filter = contracts.lotteryNFT.filters.Transfer();
      const events = await contracts.lotteryNFT.queryFilter(filter, 0, 'latest');

      // Calculate holdings for each address
      const holdingsMap = new Map();
      const zeroAddress = '0x0000000000000000000000000000000000000000';

      for (const event of events) {
        const from = event.args.from;
        const to = event.args.to;

        // If from is not zero address, decrease holdings
        if (from !== zeroAddress) {
          const fromHoldings = holdingsMap.get(from.toLowerCase()) || 0;
          holdingsMap.set(from.toLowerCase(), fromHoldings - 1);
        }

        // If to is not zero address, increase holdings
        if (to !== zeroAddress) {
          const toHoldings = holdingsMap.get(to.toLowerCase()) || 0;
          holdingsMap.set(to.toLowerCase(), toHoldings + 1);
        }
      }

      // Convert to array and sort
      const rankings = Array.from(holdingsMap.entries())
        .filter(([addr, count]) => count > 0)
        .map(([address, count]) => ({ address, count }))
        .sort((a, b) => b.count - a.count)
        .slice(0, limit);

      return rankings;
    } catch (error) {
      console.error('Failed to get holder rankings:', error);
      return [];
    }
  };

  /**
   * Get overall statistics overview
   */
  const getOverviewStats = async () => {
    if (!contracts.lotteryNFT || !contracts.prizePool || !contracts.systemConfig) return null;

    try {
      const [totalMinted, totalSupply, currentDrawId, lastDrawTime, drawInterval] = await Promise.all([
        contracts.lotteryNFT.totalMinted(),
        contracts.systemConfig.getTotalNFTSupply(),
        contracts.prizePool.currentDrawId(),
        contracts.prizePool.lastDrawTime(),
        contracts.systemConfig.getDrawInterval()
      ]);

      // Get current draw sales data
      const currentSales = await getSalesStats(currentDrawId.toString());

      // Get marketplace statistics
      let activeOrderCount = 0;
      let totalTradeCount = 0;
      try {
        activeOrderCount = await contracts.marketplace.getActiveOrderCount();
        totalTradeCount = await contracts.marketplace.getTotalTradeCount();
      } catch (e) {
        console.log('Failed to get market stats, contract may not support');
      }

      return {
        totalMinted: totalMinted.toString(),
        totalSupply: totalSupply.toString(),
        remaining: (parseInt(totalSupply.toString()) - parseInt(totalMinted.toString())).toString(),
        currentDrawId: currentDrawId.toString(),
        lastDrawTime: lastDrawTime.toString(),
        drawInterval: drawInterval.toString(),
        currentSales,
        activeOrderCount: activeOrderCount.toString(),
        totalTradeCount: totalTradeCount.toString()
      };
    } catch (error) {
      console.error('Failed to get overview stats:', error);
      return null;
    }
  };

  // ============ Load Public Data (No wallet required) ============

  const loadPublicData = async () => {
    try {
      // Use read-only provider to connect to node
      const rpcUrl = NETWORK_INFO.rpcUrl || 'http://127.0.0.1:8545';
      const readOnlyProvider = new ethers.JsonRpcProvider(rpcUrl);

      const systemConfigContract = new ethers.Contract(
        CONTRACT_ADDRESSES.systemConfig,
        SYSTEM_CONFIG_ABI,
        readOnlyProvider
      );

      const lotteryNFT = new ethers.Contract(
        CONTRACT_ADDRESSES.lotteryNFT,
        LOTTERY_NFT_V3_ABI,
        readOnlyProvider
      );

      const prizePool = new ethers.Contract(
        CONTRACT_ADDRESSES.prizePool,
        PRIZE_POOL_ABI,
        readOnlyProvider
      );

      const [mintPrice, drawInterval, currentDrawId, lastDrawTime, totalMinted, totalSupply] = await Promise.all([
        systemConfigContract.getMintPrice(),
        systemConfigContract.getDrawInterval(),
        prizePool.currentDrawId(),
        prizePool.lastDrawTime(),
        lotteryNFT.totalMinted(),
        systemConfigContract.getTotalNFTSupply()
      ]);

      setSystemConfig({
        usdtPrice: ethers.formatUnits(mintPrice[0], 6),
        ethPrice: ethers.formatEther(mintPrice[1]),
        usdtEnabled: mintPrice[2],
        ethEnabled: mintPrice[3],
        drawInterval: drawInterval.toString()
      });

      // Fetch prize pool data from backend API (shared with go-uniapp)
      let prizePoolBalance = '0';
      let dividendPoolBalance = '0';
      let projectFee = '0';
      try {
        const backendUrl = process.env.REACT_APP_BACKEND_API_URL || 'http://localhost:8085';
        const response = await fetch(`${backendUrl}/api/powerball/prize-pool/total`);
        if (response.ok) {
          const data = await response.json();
          if (data.code === 1 && data.data) {
            prizePoolBalance = data.data.prize_pool?.toFixed(2) || '0';
            dividendPoolBalance = data.data.dividend_pool?.toFixed(2) || '0';
            projectFee = data.data.project_fee?.toFixed(2) || '0';
            console.log('Prize pool data from backend (public):', data.data);
          }
        }
      } catch (e) {
        console.warn('Failed to get prize pool from backend API:', e);
      }

      setNftStats({
        totalMinted: totalMinted.toString(),
        totalSupply: totalSupply.toString(),
        currentDrawId: currentDrawId.toString(),
        lastDrawTime: lastDrawTime.toString(),
        prizePoolBalance: prizePoolBalance,
        dividendPoolBalance: dividendPoolBalance,
        projectFee: projectFee
      });

      console.log('Public data loaded:', {
        drawInterval: drawInterval.toString(),
        lastDrawTime: lastDrawTime.toString(),
        currentDrawId: currentDrawId.toString(),
        prizePoolBalance: prizePoolBalance
      });
    } catch (error) {
      console.error('Failed to load public data:', error);
    }
  };

  // ============ Initialization ============

  useEffect(() => {
    // Load public data first (no wallet required)
    loadPublicData();
    // Then check wallet connection
    checkConnection();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ============ Context Value ============

  const value = {
    // State
    account,
    provider,
    signer,
    contracts,
    network,
    isConnecting,
    systemConfig,
    balances,
    nftStats,
    PaymentMethod,

    // Wallet operations
    connectWallet,
    disconnectWallet,

    // NFT Minting
    mintRandomUSDT,
    mintRandomETH,
    mintWithNumberUSDT,
    mintWithNumberETH,

    // NFT queries
    getUserNFTs,

    // Dividend functions
    getClaimableDividends,
    claimDividend,
    claimDividendsBatch,
    getUserUnclaimedDraws,

    // Prize pool functions
    getDrawResult,
    claimPrize,
    claimPrizesBatch,

    // Marketplace functions
    createOrderUSDT,
    createOrderETH,
    buyWithUSDT,
    buyWithETH,
    cancelOrder,
    getActiveOrders,
    getRecentTrades,

    // Refresh data
    refreshBalances: () => contracts.usdtToken && provider && account
      ? loadBalances(contracts.usdtToken, provider, account)
      : null,
    refreshSystemConfig: () => contracts.systemConfig && contracts.lotteryNFT && contracts.prizePool
      ? loadSystemConfig(contracts.systemConfig, contracts.lotteryNFT, contracts.prizePool)
      : null,

    // Admin functions
    isAdmin,
    requestDraw,
    getAdminInfo,
    OWNER_ADDRESS,

    // Admin statistics functions
    getSalesStats,
    getAllSalesStats,
    getAllDividendStats,
    getNFTHolderRankings,
    getOverviewStats
  };

  return (
    <Web3ContextV3.Provider value={value}>
      {children}
    </Web3ContextV3.Provider>
  );
};
